import { IcoEye16, IcoFileText16, IcoSparkles16 } from '@onefootprint/icons';
import type { ResourceContext } from '@onefootprint/request-types/percy';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { SuggestedAction } from '../components/suggested-actions';
import { presentResource } from '../utils/resource-presentation';
import { CURSOR_MARKER } from '../utils/seed-text';
import { useResourceContextSelection } from './use-resource-context-selection';

export const resolveSuggestedPrompt = (
  prompt: string,
  context: ResourceContext,
  t: TFunction<'copilot'>,
): string | null => {
  if (!prompt.includes(CURSOR_MARKER)) return prompt;
  const name = presentResource(context, t)?.name;
  if (!name) return null;
  return prompt.replace(CURSOR_MARKER, name);
};

export const useSuggestedActions = (): SuggestedAction[] => {
  const { t } = useTranslation('ask-percy');
  const { t: tCopilot } = useTranslation('copilot');
  const { effectiveContext } = useResourceContextSelection();

  if (!effectiveContext) return [];

  if (effectiveContext.kind === 'agent') {
    return (['review', 'missing', 'rules'] as const).map(key => {
      const prompt = t($ => $.actionPills.agentBuilder[key]);
      return { id: `agent_${key}`, label: prompt, icon: IcoSparkles16, prompt };
    });
  }

  if (effectiveContext.kind === 'playbook') {
    return (['explain', 'review', 'gaps'] as const).map(key => {
      const prompt = t($ => $.actionPills.playbookDetail[key]);
      return { id: `playbook_${key}`, label: prompt, icon: IcoSparkles16, prompt };
    });
  }

  if (effectiveContext.kind === 'entity') {
    const candidates: SuggestedAction[] = [
      {
        id: 'summarize_entity',
        label: t($ => $.actionPills.summarize),
        icon: IcoEye16,
        prompt: t($ => $.actionPills.directActions.summarize.prompt),
      },
      {
        id: 'generate_report',
        label: t($ => $.actionPills.generateReport),
        icon: IcoFileText16,
        prompt: t($ => $.actionPills.directActions.generateReport.prompt),
      },
    ];
    return candidates.flatMap(action => {
      const prompt = resolveSuggestedPrompt(action.prompt, effectiveContext, tCopilot);
      return prompt ? [{ ...action, prompt }] : [];
    });
  }

  return [];
};
